import { useMutation as useTanstackMutation } from '@tanstack/react-query'
import qs from 'qs'
import { apiBaseUrl, apiEndpoint } from '~/constants/api'
import { useAuthStore } from '~/stores/auth'
import type { Todo } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import { useToast } from './use-toast'

type UseMutationOptions<TData, TVariables> = {
  mutationKey: MutationKey
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  idParams?: Todo
  queries?: Record<string, Todo>
  withToken?: boolean
  responseType?: 'json' | 'arraybuffer'
  options?: {
    onSuccess?: (data: TData, variables: TVariables) => void
    onError?: (error: Error) => void
  }
  onError?: (error: Error) => void
}

async function refreshTokens(accessToken: string, refreshToken: string) {
  const url = `${apiBaseUrl}${apiEndpoint.mutation[MutationKey.REFRESH_TOKEN]()}`
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken, refreshToken }),
  })

  if (!response.ok) {
    throw new Error('Token refresh failed')
  }

  return response.json()
}

export function useMutation<TData = Todo, TVariables = Todo>({
  mutationKey,
  method,
  idParams,
  queries,
  withToken = true,
  responseType = 'json',
  options,
  onError,
}: UseMutationOptions<TData, TVariables>) {
  const { accessToken, refreshToken, setTokens, clearTokens } = useAuthStore()
  const { showError } = useToast()

  return useTanstackMutation<TData, Error, TVariables>({
    mutationKey: [mutationKey, idParams],
    mutationFn: async (variables: TVariables) => {
      const endpoint = apiEndpoint.mutation[mutationKey](idParams)
      const queryString = queries ? `?${qs.stringify(queries, { arrayFormat: 'brackets' })}` : ''
      const url = `${apiBaseUrl}${endpoint}${queryString}`

      const isFormData = variables instanceof FormData

      const headers: Record<string, string> = {}

      if (!isFormData) {
        headers['Content-Type'] = 'application/json'
      }

      if (withToken && accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`
      }

      const body = isFormData ? (variables as FormData) : JSON.stringify(variables)

      let response = await fetch(url, { method, headers, body })

      if (response.status === 401 && withToken && refreshToken) {
        try {
          const data = await refreshTokens(accessToken, refreshToken)
          setTokens(data.tokens.accessToken, data.tokens.refreshToken)

          headers['Authorization'] = `Bearer ${data.tokens.accessToken}`
          response = await fetch(url, { method, headers, body })
        } catch {
          clearTokens()
          showError('Session expired, please login again')
          throw new Error('Token refresh failed')
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const message = errorData.message || 'Something went wrong'
        throw new Error(message)
      }

      if (responseType === 'arraybuffer') {
        return (await response.arrayBuffer()) as unknown as TData
      }

      if (response.status === 204) {
        return {} as TData
      }

      return response.json()
    },
    onSuccess: options?.onSuccess,
    onError: (error: Error) => {
      if (onError) {
        onError(error)
      } else if (options?.onError) {
        options.onError(error)
      } else {
        showError(error.message)
      }
    },
  })
}
